import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Users,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Loader2,
  UserX,
  UserCheck,
  Archive,
  AlertCircle
} from 'lucide-react';

// Blocked free email domains - must match backend list
const BLOCKED_FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'ymail.com', 'rocketmail.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aim.com',
  'mail.com', 'protonmail.com', 'proton.me', 'zoho.com',
  'gmx.com', 'gmx.net', 'yandex.com', 'tutanota.com', 'fastmail.com'
]);

const isBlockedEmailDomain = (email) => {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return BLOCKED_FREE_EMAIL_DOMAINS.has(domain);
};

const FUNCTION_QUALIFICATIONS = [
  'Confined Space Entrant and Attendant',
  'Hopper Car General Inspection & Repair',
  'Hopper Car Interior Coatings Inspection & Repair',
  'Incoming Material Inspections',
  'Railcar Jacking',
  'Single Car Air Brake Testing',
  'Tank Car General Inspection & Repair',
  'Tank Car Service Equipment Application & Removal',
  'Tank Car Interior Coatings Inspection & Repair',
  'Tank Car Service Equipment Teardown & Rebuild'
];

// NDT Qualifications - codes mapped to labels, alphabetically by code
// Today as YYYY-MM-DD — used to constrain past-only date pickers (hire date, etc.).
const TODAY_ISO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

const NDT_QUALIFICATIONS = [
  { code: 'AE', label: 'AE (Acoustic Emission)' },
  { code: 'ET', label: 'ET (Eddy Current)' },
  { code: 'LT', label: 'LT (Leak Testing)' },
  { code: 'MT', label: 'MT (Magnetic Particle)' },
  { code: 'PCT', label: 'PCT (Pressure Change Testing)' },
  { code: 'PT', label: 'PT (Liquid Penetrant)' },
  { code: 'RT', label: 'RT (Radiographic)' },
  { code: 'RVI', label: 'RVI (Remote Visual Inspection)' },
  { code: 'UT', label: 'UT (Ultrasonic)' },
  { code: 'UTT', label: 'UTT (Ultrasonic Thickness)' },
  { code: 'VTG', label: 'VTG (Visual Testing General)' },
  { code: 'VTW', label: 'VTW (Visual Testing Welds)' }
];

// Migration map for old codes to new codes.
// NOTE: Ambiguous bare "VT" entries are intentionally NOT auto-migrated. They are passed through
// unchanged so the backend startup migration / admin review can resolve them explicitly. The
// previous default ('VT' -> 'VTG') has been removed to avoid silently misclassifying legacy data.
const NDT_CODE_MIGRATION = {
  'BT (Beta Backscatter)': 'LT',
  'BT': 'LT',
  'PCT (Pulse Echo Contact)': 'PCT',
  'RFI (Remote Field Inspection)': 'VTW',
  'RFI': 'VTW',
  // Explicit VT label variants → new codes
  'VT - General Inspection': 'VTG',
  'VT (General Inspection)': 'VTG',
  'VT - Weld Inspection': 'VTW',
  'VT (Weld Inspection)': 'VTW'
};

// Helper to migrate old NDT codes to new codes
const migrateNdtCodes = (codes) => {
  if (!codes || !Array.isArray(codes)) return [];
  return codes.map(code => NDT_CODE_MIGRATION[code] || code);
};

// Welder Qualifications - Two-column structure
const WELDER_PROCESSES = ['SMAW', 'FCAW', 'GMAW', 'GTAW'];
const WELDER_CERTIFICATIONS = [
  { id: 'AWS_D15', label: 'AWS D15.1' },
  { id: 'AAR', label: 'AAR' }
];

const EQUIPMENT_QUALIFICATIONS = ['Forklift', 'Telehandlers', 'Cranes'];

const emptyFormData = {
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  phone: '',
  job_title: '',
  role: 'TECHNICIAN',
  hire_date: new Date().toISOString().split('T')[0],
  termination_date: '',
  facility_ids: [],
  address: '',
  city: '',
  state: '',
  zip_code: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  technician_id: '',
  // 2026-06-05 — Opt-in tech capability. Toggled by Operator+ from the Edit
  // Technician modal; default OFF for new technicians.
  can_create_service_requests: false,
  function_qualifications: [],
  ndt_qualifications: [],
  welder_qualifications: [],
  equipment_qualifications: []
};

// Sort direction indicator icon for the User Management table header
// (defined at module scope so React can preserve component identity across renders).
const SortIcon = ({ field, sortBy, sortDir }) => {
  if (sortBy !== field) return <ArrowUpDown className="inline w-3 h-3 ml-1 text-[#808080]" />;
  return sortDir === 'asc'
    ? <ChevronUp className="inline w-3 h-3 ml-1 text-[#005EF8]" />
    : <ChevronDown className="inline w-3 h-3 ml-1 text-[#005EF8]" />;
};

const UserManagementPage = () => {
  const { user: currentUser, api, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // Phase 4 Item 1 — Facility filter + column sorting.
  const [facilityFilter, setFacilityFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [qualificationsOpen, setQualificationsOpen] = useState(false);
  const [techIdError, setTechIdError] = useState('');
  const [techIdChecking, setTechIdChecking] = useState(false);
  
  // Role change confirmation dialog state
  const [roleChangeConfirm, setRoleChangeConfirm] = useState(null);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  
  // USER LIFECYCLE: Terminate/Reactivate confirmation state
  const [terminateConfirm, setTerminateConfirm] = useState(null);
  const [reactivateConfirm, setReactivateConfirm] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  
  // Show inactive users toggle
  const [showInactive, setShowInactive] = useState(false);
  
  // Phase 2: NDT method removal confirmation. Tracks the method being unchecked
  // that already has saved NDT records, so we can warn before soft-deleting.
  const [originalNdt, setOriginalNdt] = useState([]);
  const [ndtRemoveConfirm, setNdtRemoveConfirm] = useState(null); // { code, label } | null

  // Welding Personnel: confirmation when unchecking a welder qualification that
  // already has per-row detail data (filler/material/positions/etc.). Only
  // surfaces the dialog when the backend confirms the row is non-empty.
  const [originalWelder, setOriginalWelder] = useState([]);
  const [welderRemoveConfirm, setWelderRemoveConfirm] = useState(null); // { value, label } | null

  // Generate technician ID from names
  const generateTechnicianId = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter first and last name first');
      return;
    }
    try {
      const response = await api.post('/technicians/generate-id', {
        first_name: formData.first_name,
        last_name: formData.last_name
      });
      setFormData(prev => ({ ...prev, technician_id: response.data.technician_id }));
      setTechIdError('');
    } catch (error) {
      console.error('Failed to generate technician ID:', error);
      // Fallback: generate client-side
      const prefix = (formData.first_name[0] + formData.last_name[0]).toUpperCase();
      const suffix = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
      setFormData(prev => ({ ...prev, technician_id: prefix + suffix }));
    }
  };

  // Check if technician ID is available
  const checkTechnicianId = async (id) => {
    if (!id || id.length < 3) {
      setTechIdError('');
      return;
    }
    setTechIdChecking(true);
    try {
      const response = await api.post('/technicians/check-id', {
        technician_id: id,
        exclude_user_id: editingUser?.id || null
      });
      if (!response.data.available) {
        setTechIdError('This Technician ID is already in use. Please choose another.');
      } else {
        setTechIdError('');
      }
    } catch (error) {
      console.error('Failed to check technician ID:', error);
    } finally {
      setTechIdChecking(false);
    }
  };

  // Auto-generate Technician ID when role changes to TECHNICIAN and names are present
  useEffect(() => {
    if (formData.role === 'TECHNICIAN' && 
        formData.first_name && 
        formData.last_name && 
        !formData.technician_id && 
        !editingUser) {
      generateTechnicianId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role, formData.first_name, formData.last_name, editingUser, formData.technician_id]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== 'ALL') params.role = roleFilter;
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (search) params.search = search;
      const response = await api.get('/users', { params });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api, roleFilter, statusFilter, search]);

  // Phase 4 Item 1 — client-side facility filtering + column sorting layered
  // on top of the server-filtered result set. Facility filter is client-side
  // because a single user may belong to multiple facilities and the /users
  // endpoint doesn't currently accept a facility param.
  const facilitySortValue = (u) => {
    if (u.role === 'ADMIN') return '';               // sorts first
    if ((u.facilities || []).length > 1) return 'zzz_multiple'; // sorts last
    return (u.facilities?.[0]?.name || '').toLowerCase();
  };

  const visibleUsers = useMemo(() => {
    const facFiltered = facilityFilter === 'ALL'
      ? users
      : users.filter(u => u.role === 'ADMIN'
          || (u.facilities || []).some(f => f.id === facilityFilter));

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...facFiltered].sort((a, b) => {
      let av;
      let bv;
      if (sortBy === 'facility') {
        av = facilitySortValue(a);
        bv = facilitySortValue(b);
      } else {
        av = (a[sortBy] || '').toString().toLowerCase();
        bv = (b[sortBy] || '').toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [users, facilityFilter, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchUsers();
    fetchFacilities();
  }, [fetchUsers, fetchFacilities]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      const existingNdt = migrateNdtCodes(user.qualifications?.ndt_qualifications || []);
      setOriginalNdt(existingNdt);
      setOriginalWelder(user.qualifications?.welder_qualifications || []);
      setFormData({
        first_name: user.first_name || '',
        middle_name: user.middle_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        job_title: user.job_title || '',
        role: user.role || 'TECHNICIAN',
        hire_date: user.hire_date || '',
        termination_date: user.termination_date || '',
        facility_ids: user.facility_ids || [],
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        technician_id: user.technician_id || '',
        can_create_service_requests: !!user.can_create_service_requests,
        function_qualifications: user.qualifications?.function_qualifications || [],
        ndt_qualifications: existingNdt,
        welder_qualifications: user.qualifications?.welder_qualifications || [],
        equipment_qualifications: user.qualifications?.equipment_qualifications || []
      });
    } else {
      setEditingUser(null);
      setOriginalNdt([]);
      setOriginalWelder([]);
      setFormData(emptyFormData);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData(emptyFormData);
    setAdditionalOpen(false);
    setQualificationsOpen(false);
    setTechIdError('');
  };

  const handleChange = (field, value) => {
    // Handle role change with confirmation dialog when editing existing user
    if (field === 'role' && editingUser && value !== formData.role) {
      // Store the pending role change
      setPendingRoleChange(value);
      setRoleChangeConfirm({
        from: formData.role,
        to: value,
        hasMultipleFacilities: formData.facility_ids.length > 1
      });
      return; // Don't update formData yet - wait for confirmation
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear tech ID error when editing the field
    if (field === 'technician_id') {
      setTechIdError('');
    }
  };
  
  // Handle role change confirmation
  const handleRoleChangeConfirm = () => {
    const newRole = pendingRoleChange;
    
    // If changing TO Technician or Operator with multiple facilities, keep only the first one
    let newFacilityIds = formData.facility_ids;
    if ((newRole === 'TECHNICIAN' || newRole === 'OPERATOR') && formData.facility_ids.length > 1) {
      newFacilityIds = [formData.facility_ids[0]];
      toast.info('Multiple facilities were assigned. Kept the first facility for this role.');
    }
    
    // If changing TO Admin, clear facility assignments (Admins have access to all)
    if (newRole === 'ADMIN') {
      newFacilityIds = [];
    }
    
    setFormData(prev => ({ 
      ...prev, 
      role: newRole,
      facility_ids: newFacilityIds
    }));
    
    setRoleChangeConfirm(null);
    setPendingRoleChange(null);
  };
  
  const handleRoleChangeCancel = () => {
    setRoleChangeConfirm(null);
    setPendingRoleChange(null);
  };

  const handleTechnicianIdChange = (value) => {
    // Only allow uppercase letters and numbers
    const cleanValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    setFormData(prev => ({ ...prev, technician_id: cleanValue }));
    setTechIdError('');
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
  };

  // Phase 2: NDT method toggle with soft-delete confirmation when an
  // existing (already-saved) method is being unchecked.
  const handleNdtMethodChange = (qual, checked) => {
    if (!checked && originalNdt.includes(qual.code) && editingUser) {
      // User is removing a method that already has records — confirm first.
      setNdtRemoveConfirm({ code: qual.code, label: qual.label });
      return;
    }
    handleCheckboxChange('ndt_qualifications', qual.code, checked);
  };

  const confirmNdtRemoval = () => {
    if (!ndtRemoveConfirm) return;
    handleCheckboxChange('ndt_qualifications', ndtRemoveConfirm.code, false);
    setNdtRemoveConfirm(null);
  };

  const cancelNdtRemoval = () => setNdtRemoveConfirm(null);

  // Welder qualification toggle — when unchecking a previously-saved qualification,
  // ask the backend whether the detail row has user-entered data. Only then prompt.
  const handleWelderQualChange = async (value, checked) => {
    const wasOriginallyChecked = originalWelder.includes(value);
    if (!checked && wasOriginallyChecked && editingUser) {
      try {
        const { data } = await api.get(
          `/lms/welding-personnel/${editingUser.id}/has-qualification-detail-data`,
          { params: { qualification: value } }
        );
        if (data?.has_data) {
          // Friendly label for the dialog (e.g. "SMAW · AAR")
          const standard = value.startsWith('AWS_D15_') ? 'AWS D15.1' : 'AAR';
          const process = value.replace(/^AWS_D15_|^AAR_/, '');
          setWelderRemoveConfirm({ value, label: `${process} · ${standard}` });
          return;
        }
      } catch (err) {
        // Best-effort: if the lookup fails, fall through and just uncheck.
        // The backend uncheck still triggers the soft-delete sync.
      }
    }
    handleCheckboxChange('welder_qualifications', value, checked);
  };

  const confirmWelderRemoval = () => {
    if (!welderRemoveConfirm) return;
    handleCheckboxChange('welder_qualifications', welderRemoveConfirm.value, false);
    setWelderRemoveConfirm(null);
  };

  const cancelWelderRemoval = () => setWelderRemoveConfirm(null);

  const handleFacilityChange = (facilityId, checked) => {
    // Technicians and Operators can only have one facility
    if (formData.role === 'OPERATOR' || formData.role === 'TECHNICIAN') {
      setFormData(prev => ({
        ...prev,
        facility_ids: checked ? [facilityId] : []
      }));
    } else {
      // Managers can have multiple facilities
      handleCheckboxChange('facility_ids', facilityId, checked);
    }
  };
  
  // Handle single-select facility change for Technicians/Operators
  const handleSingleFacilityChange = (facilityId) => {
    setFormData(prev => ({
      ...prev,
      facility_ids: facilityId ? [facilityId] : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Block free email domains for non-technician roles (work email required)
    // Technicians are allowed to use free email domains (Gmail, Yahoo, etc.)
    if (formData.role !== 'TECHNICIAN' && isBlockedEmailDomain(formData.email)) {
      toast.error('Please use a work email. Free email providers (Gmail, Yahoo, Outlook, etc.) are not allowed for this role.');
      return;
    }

    // Facility validation based on role
    if (formData.role === 'ADMIN') {
      // Admins don't need facility assignment
    } else if (formData.role === 'TECHNICIAN' || formData.role === 'OPERATOR') {
      // Technicians and Operators need exactly one facility
      if (formData.facility_ids.length === 0) {
        toast.error('Please assign a facility');
        return;
      }
      if (formData.facility_ids.length > 1) {
        toast.error('This role only allows one facility. Please select one.');
        return;
      }
    } else if (formData.role === 'MANAGER') {
      // Managers need at least one facility
      if (formData.facility_ids.length === 0) {
        toast.error('Please assign at least one facility');
        return;
      }
    }

    // Technician ID validation
    if (formData.role === 'TECHNICIAN') {
      if (!formData.technician_id || formData.technician_id.length < 3) {
        toast.error('Technician ID is required (minimum 3 characters)');
        return;
      }
      if (techIdError) {
        toast.error('Please resolve the Technician ID error');
        return;
      }
    }

    setSaving(true);
    try {
      // Build a clean payload — strip empty `technician_id` so Pydantic min_length=3 doesn't trip
      // when saving a non-Technician user.
      const buildPayload = (data) => {
        const payload = { ...data };
        if (!payload.technician_id) {
          delete payload.technician_id;
        }
        return payload;
      };
      const payload = buildPayload(formData);
      
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload);
        
        // Phase 2: Send qualifications for all roles.
        // - Technicians: full qualifications payload.
        // - Operators/Managers/Admins: NDT Certifications only (other fields ignored server-side).
        await api.put(`/users/${editingUser.id}/qualifications`, {
          function_qualifications: formData.role === 'TECHNICIAN' ? formData.function_qualifications : [],
          ndt_qualifications: formData.ndt_qualifications,
          welder_qualifications: formData.role === 'TECHNICIAN' ? formData.welder_qualifications : [],
          equipment_qualifications: formData.role === 'TECHNICIAN' ? formData.equipment_qualifications : []
        });
        
        toast.success('User updated successfully!');
      } else {
        const response = await api.post('/users', payload);
        
        await api.put(`/users/${response.data.user_id}/qualifications`, {
          function_qualifications: formData.role === 'TECHNICIAN' ? formData.function_qualifications : [],
          ndt_qualifications: formData.ndt_qualifications,
          welder_qualifications: formData.role === 'TECHNICIAN' ? formData.welder_qualifications : [],
          equipment_qualifications: formData.role === 'TECHNICIAN' ? formData.equipment_qualifications : []
        });
        
        toast.success(response.data.message);
      }
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      // FastAPI/Pydantic returns `detail` as either a string or a list of objects.
      // Always reduce to a string before showing the toast — otherwise React crashes.
      const detail = error.response?.data?.detail;
      let message = 'Failed to save user';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail
          .map(d => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
          .join('; ');
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || JSON.stringify(detail);
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success('User deleted successfully');
      setDeleteId(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // USER LIFECYCLE: Terminate user
  const handleTerminate = async () => {
    if (!terminateConfirm) return;
    setLifecycleLoading(true);
    try {
      await api.post(`/users/${terminateConfirm.id}/terminate`);
      toast.success(`${terminateConfirm.first_name} ${terminateConfirm.last_name} has been terminated`);
      setTerminateConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate user');
    } finally {
      setLifecycleLoading(false);
    }
  };

  // USER LIFECYCLE: Reactivate user
  const handleReactivate = async () => {
    if (!reactivateConfirm) return;
    setLifecycleLoading(true);
    try {
      const response = await api.post(`/users/${reactivateConfirm.id}/reactivate`);
      toast.success(`${reactivateConfirm.first_name} ${reactivateConfirm.last_name} has been reactivated`);
      setReactivateConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    } finally {
      setLifecycleLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'user-status-active';
      case 'TERMINATED':
        return 'user-status-terminated';
      default:
        return 'user-status-pending';
    }
  };

  const availableRoles = () => {
    if (currentUser?.role === 'ADMIN') {
      return ['TECHNICIAN', 'OPERATOR', 'MANAGER', 'ADMIN'];
    } else if (currentUser?.role === 'MANAGER') {
      return ['TECHNICIAN', 'OPERATOR'];
    } else {
      return ['TECHNICIAN'];
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">User Management</h1>
          <p className="text-[#5A5A5A] mt-1">
            Manage team members and their permissions
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              placeholder="Search users by name, email, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
        </form>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Roles</SelectItem>
            <SelectItem value="TECHNICIAN">Technician</SelectItem>
            <SelectItem value="OPERATOR">Operator</SelectItem>
            <SelectItem value="MANAGER">Manager</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Status</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="TERMINATED">Inactive / Terminated</SelectItem>
            <SelectItem value="PENDING_INVITATION">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={facilityFilter} onValueChange={setFacilityFilter}>
          <SelectTrigger className="w-[200px]" data-testid="facility-filter">
            <SelectValue placeholder="Filter by facility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Facilities</SelectItem>
            {facilities.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Inactive Users Info Banner */}
      {statusFilter === 'TERMINATED' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <Archive className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">
            Showing inactive/terminated users. These users cannot log in but their historical data is preserved for audit.
          </span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8] mx-auto"></div>
              <p className="text-[#5A5A5A] mt-4">Loading...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#323033] mb-2">No users found</h3>
              <p className="text-[#5A5A5A] mb-6">
                Add team members to get started
              </p>
              <Button
                onClick={() => handleOpenModal()}
                className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('first_name')}
                      data-testid="sort-first-name"
                    >
                      First Name<SortIcon field="first_name" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('last_name')}
                      data-testid="sort-last-name"
                    >
                      Last Name<SortIcon field="last_name" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('email')}
                      data-testid="sort-email"
                    >
                      Email<SortIcon field="email" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('job_title')}
                      data-testid="sort-job-title"
                    >
                      Job Title<SortIcon field="job_title" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('role')}
                      data-testid="sort-role"
                    >
                      Role<SortIcon field="role" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('facility')}
                      data-testid="sort-facility"
                    >
                      Facility<SortIcon field="facility" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('status')}
                      data-testid="sort-status"
                    >
                      Status<SortIcon field="status" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="font-medium text-[#323033]">{user.first_name}</td>
                      <td>{user.last_name}</td>
                      <td>{user.email}</td>
                      <td>{user.job_title || '-'}</td>
                      <td className="capitalize">{user.role?.toLowerCase()}</td>
                      <td>
                        {user.role === 'ADMIN'
                          ? 'All'
                          : user.facilities?.length > 1
                          ? 'Multiple'
                          : user.facilities?.[0]?.name || '-'}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                          {user.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenModal(user)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenModal(user)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {/* USER LIFECYCLE: Terminate/Reactivate - Admin only */}
                            {currentUser?.role === 'ADMIN' && user.id !== currentUser?.id && (
                              <>
                                {user.status !== 'TERMINATED' ? (
                                  <DropdownMenuItem
                                    onClick={() => setTerminateConfirm(user)}
                                    className="text-amber-600"
                                    data-testid={`terminate-user-${user.id}`}
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Terminate Access
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => setReactivateConfirm(user)}
                                    className="text-green-600"
                                    data-testid={`reactivate-user-${user.id}`}
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Reactivate
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {user.id !== currentUser?.id && (
                              <DropdownMenuItem
                                onClick={() => setDeleteId(user.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update user information'
                : 'Fill in the details to add a new team member'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#323033]">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    required
                    data-testid="user-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="middle_name">Middle Name</Label>
                  <Input
                    id="middle_name"
                    value={formData.middle_name}
                    onChange={(e) => handleChange('middle_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    required
                    data-testid="user-last-name-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    disabled={!!editingUser}
                    className={isBlockedEmailDomain(formData.email) && formData.role !== 'TECHNICIAN' && !editingUser ? 'border-red-500 focus:ring-red-500' : ''}
                    data-testid="user-email-input"
                  />
                  {!editingUser && isBlockedEmailDomain(formData.email) && formData.role !== 'TECHNICIAN' ? (
                    <div className="flex items-center gap-2 text-xs text-red-600">
                      <AlertCircle className="w-3 h-3" />
                      <span>Work email required for this role</span>
                    </div>
                  ) : !editingUser && formData.role === 'TECHNICIAN' ? (
                    <p className="text-xs text-[#808080]">Personal or work email allowed for technicians</p>
                  ) : !editingUser && (
                    <p className="text-xs text-[#808080]">Use organizational/work email</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title *</Label>
                  <Input
                    id="job_title"
                    value={formData.job_title}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                    required
                    data-testid="user-job-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire_date">Hire Date *</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    max={TODAY_ISO}
                    onChange={(e) => handleChange('hire_date', e.target.value)}
                    required
                    data-testid="user-hire-date"
                  />
                </div>
              </div>
            </div>

            {/* Role & Facility */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#323033]">Role & Facility Assignment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => handleChange('role', value)}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles().map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingUser && formData.role !== editingUser.role && (
                    <p className="text-xs text-amber-600">
                      Role will be changed from {editingUser.role} to {formData.role}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Facility Assignment - varies by role */}
              {formData.role === 'ADMIN' ? (
                <div className="space-y-2">
                  <Label className="text-[#5A5A5A]">Facility Assignment</Label>
                  <p className="text-sm text-[#808080] p-3 bg-gray-50 rounded-md">
                    Admins have access to all facilities. No specific assignment needed.
                  </p>
                </div>
              ) : formData.role === 'TECHNICIAN' || formData.role === 'OPERATOR' ? (
                // Single-select dropdown for Technicians and Operators
                <div className="space-y-2">
                  <Label htmlFor="facility">Assign Facility *</Label>
                  <Select
                    value={formData.facility_ids[0] || ''}
                    onValueChange={handleSingleFacilityChange}
                  >
                    <SelectTrigger data-testid="user-facility-select">
                      <SelectValue placeholder="Select a facility" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilities.map((facility) => (
                        <SelectItem key={facility.id} value={facility.id}>
                          {facility.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#5A5A5A]">
                    {formData.role === 'TECHNICIAN' ? 'Technicians' : 'Operators'} can only be assigned to one facility.
                  </p>
                </div>
              ) : (
                // Multi-select checkboxes for Managers
                <div className="space-y-2">
                  <Label>Assign Facilities *</Label>
                  <div className="border rounded-md p-4 space-y-2 max-h-40 overflow-y-auto">
                    {facilities.map((facility) => (
                      <div key={facility.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`facility_${facility.id}`}
                          checked={formData.facility_ids.includes(facility.id)}
                          onCheckedChange={(checked) => handleFacilityChange(facility.id, checked)}
                        />
                        <Label htmlFor={`facility_${facility.id}`} className="cursor-pointer">
                          {facility.name}
                        </Label>
                      </div>
                    ))}
                    {facilities.length === 0 && (
                      <p className="text-sm text-[#5A5A5A]">No facilities available</p>
                    )}
                  </div>
                  <p className="text-xs text-[#5A5A5A]">
                    Managers can be assigned to multiple facilities.
                  </p>
                </div>
              )}
            </div>

            {/* Additional Information */}
            <Collapsible open={additionalOpen} onOpenChange={setAdditionalOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between">
                  <span>Additional Information</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${additionalOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => handleChange('zip_code', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
                    <Input
                      id="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
                    <Input
                      id="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => handleChange('emergency_contact_phone', e.target.value)}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Technician Qualifications */}
            {formData.role === 'TECHNICIAN' && (
              <Collapsible open={qualificationsOpen} onOpenChange={setQualificationsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between">
                    <span>Technician Details</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${qualificationsOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* Technician ID */}
                  <div className="space-y-2 bg-[#FAFAFA] border rounded-md p-4">
                    <Label htmlFor="technician_id" className="font-semibold text-[#323033]">
                      Technician ID *
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          id="technician_id"
                          value={formData.technician_id}
                          onChange={(e) => handleTechnicianIdChange(e.target.value)}
                          onBlur={() => checkTechnicianId(formData.technician_id)}
                          placeholder="e.g., JS1234"
                          maxLength={12}
                          className={techIdError ? 'border-red-500' : ''}
                          data-testid="technician-id-input"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={generateTechnicianId}
                        disabled={!formData.first_name || !formData.last_name}
                        className="shrink-0"
                        data-testid="generate-tech-id-btn"
                      >
                        Generate
                      </Button>
                    </div>
                    {techIdError && (
                      <p className="text-sm text-red-500" data-testid="tech-id-error">{techIdError}</p>
                    )}
                    {techIdChecking && (
                      <p className="text-sm text-[#5A5A5A]">Checking availability...</p>
                    )}
                    <p className="text-xs text-[#5A5A5A]">
                      Required unique identifier for this technician. Used for compliance and reporting.
                    </p>
                  </div>

                  {/* 2026-06-05 — Per-tech permission: can create Service Requests. */}
                  <div className="border rounded-md p-3 flex items-start gap-3 bg-[#FAFAFA]">
                    <Checkbox
                      id="can_create_service_requests"
                      checked={!!formData.can_create_service_requests}
                      onCheckedChange={(checked) => setFormData((p) => ({ ...p, can_create_service_requests: !!checked }))}
                      data-testid="can-create-sr-toggle"
                    />
                    <div className="flex-1">
                      <Label htmlFor="can_create_service_requests" className="cursor-pointer font-medium">
                        Can create Service Requests
                      </Label>
                      <p className="text-xs text-[#5A5A5A] mt-0.5">
                        When enabled, this technician can create SRs (Onsite / field work). Scoped to their assigned facility. Default off.
                      </p>
                    </div>
                  </div>

                  {/* Function Qualifications */}
                  <div className="space-y-2">
                    <Label>Function-Specific Qualifications</Label>
                    <div className="border rounded-md p-4 space-y-2 max-h-40 overflow-y-auto">
                      {FUNCTION_QUALIFICATIONS.map((qual) => (
                        <div key={qual} className="flex items-center space-x-2">
                          <Checkbox
                            id={`func_${qual}`}
                            checked={formData.function_qualifications.includes(qual)}
                            onCheckedChange={(checked) => handleCheckboxChange('function_qualifications', qual, checked)}
                          />
                          <Label htmlFor={`func_${qual}`} className="cursor-pointer text-sm">{qual}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* NDT Qualifications — Technicians (scheduling-eligible) */}
                  <div className="space-y-2">
                    <Label>NDT Qualifications</Label>
                    <p className="text-xs text-[#5A5A5A]">
                      Methods checked here make this technician eligible for NDT-related work scheduling.
                    </p>
                    <div className="border rounded-md p-4 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {NDT_QUALIFICATIONS.map((qual) => (
                        <div key={qual.code} className="flex items-center space-x-2">
                          <Checkbox
                            id={`ndt_${qual.code}`}
                            checked={formData.ndt_qualifications.includes(qual.code)}
                            onCheckedChange={(checked) => handleNdtMethodChange(qual, checked)}
                            data-testid={`ndt-qual-${qual.code}`}
                          />
                          <Label htmlFor={`ndt_${qual.code}`} className="cursor-pointer text-sm">{qual.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Welder Qualifications - Two Column Layout */}
                  <div className="space-y-2">
                    <Label>Welder Qualifications</Label>
                    <div className="border rounded-md p-4 bg-[#FAFAFA]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* AWS D15.1 Column */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-[#323033] pb-2 border-b-2 border-[#D3D5D4]">
                            AWS D15.1
                          </h4>
                          <div className="space-y-2">
                            {WELDER_PROCESSES.map((process) => {
                              const value = `AWS_D15_${process}`;
                              return (
                                <div key={value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`welder_${value}`}
                                    checked={formData.welder_qualifications.includes(value)}
                                    onCheckedChange={(checked) => handleWelderQualChange(value, checked)}
                                    data-testid={`welder-qual-${value}`}
                                  />
                                  <Label htmlFor={`welder_${value}`} className="cursor-pointer text-sm">
                                    {process}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* AAR Column */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-[#323033] pb-2 border-b-2 border-[#D3D5D4]">
                            AAR
                          </h4>
                          <div className="space-y-2">
                            {WELDER_PROCESSES.map((process) => {
                              const value = `AAR_${process}`;
                              return (
                                <div key={value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`welder_${value}`}
                                    checked={formData.welder_qualifications.includes(value)}
                                    onCheckedChange={(checked) => handleWelderQualChange(value, checked)}
                                    data-testid={`welder-qual-${value}`}
                                  />
                                  <Label htmlFor={`welder_${value}`} className="cursor-pointer text-sm">
                                    {process}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Equipment Qualifications */}
                  <div className="space-y-2">
                    <Label>Equipment Qualifications</Label>
                    <div className="border rounded-md p-4 flex flex-wrap gap-4">
                      {EQUIPMENT_QUALIFICATIONS.map((qual) => (
                        <div key={qual} className="flex items-center space-x-2">
                          <Checkbox
                            id={`equip_${qual}`}
                            checked={formData.equipment_qualifications.includes(qual)}
                            onCheckedChange={(checked) => handleCheckboxChange('equipment_qualifications', qual, checked)}
                          />
                          <Label htmlFor={`equip_${qual}`} className="cursor-pointer text-sm">{qual}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Phase 2: NDT Certifications section for non-Technician roles (informational only) */}
            {formData.role !== 'TECHNICIAN' && (
              <div className="space-y-2 pt-4 border-t" data-testid="ndt-certifications-section">
                <Label className="text-base">NDT Certifications</Label>
                <p className="text-xs text-[#5A5A5A]">
                  Informational only — Operators / Managers / Admins do not appear in Work Schedule crew assignment, even with NDT credentials.
                </p>
                <div className="border rounded-md p-4 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {NDT_QUALIFICATIONS.map((qual) => (
                    <div key={qual.code} className="flex items-center space-x-2">
                      <Checkbox
                        id={`ndt_cert_${qual.code}`}
                        checked={formData.ndt_qualifications.includes(qual.code)}
                        onCheckedChange={(checked) => handleNdtMethodChange(qual, checked)}
                        data-testid={`ndt-cert-${qual.code}`}
                      />
                      <Label htmlFor={`ndt_cert_${qual.code}`} className="cursor-pointer text-sm">{qual.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                disabled={saving}
                data-testid="save-user-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingUser ? (
                  'Update User'
                ) : (
                  'Save User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This user will lose access immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 2: NDT method soft-delete confirmation */}
      <AlertDialog open={!!ndtRemoveConfirm} onOpenChange={(open) => { if (!open) cancelNdtRemoval(); }}>
        <AlertDialogContent data-testid="ndt-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove NDT method?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-800">
              Removing {ndtRemoveConfirm?.label || ndtRemoveConfirm?.code} will hide all existing certification records for this method. Records will be preserved and can be restored by re-enabling the method. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNdtRemoval} data-testid="ndt-remove-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmNdtRemoval}
              className="bg-red-600 hover:bg-red-700"
              data-testid="ndt-remove-confirm-btn"
            >
              Remove method
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Welding Personnel: welder qualification soft-delete confirmation.
          Only shown when the per-row qualification detail has user-entered data. */}
      <AlertDialog open={!!welderRemoveConfirm} onOpenChange={(open) => { if (!open) cancelWelderRemoval(); }}>
        <AlertDialogContent data-testid="welder-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove welding qualification?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-800">
              Removing {welderRemoveConfirm?.label} will delete the associated qualification
              details (filler metal, positions, notes, etc.) from active use. Uploaded documents
              and the welder maintenance log history will be preserved. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelWelderRemoval} data-testid="welder-remove-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmWelderRemoval}
              className="bg-red-600 hover:bg-red-700"
              data-testid="welder-remove-confirm-btn"
            >
              Remove qualification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role Change Confirmation */}
      <AlertDialog open={!!roleChangeConfirm} onOpenChange={() => handleRoleChangeCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change User Role?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to change this user&apos;s role from <strong>{roleChangeConfirm?.from}</strong> to <strong>{roleChangeConfirm?.to}</strong>.
                </p>
                <p>
                  This will update their permissions and sidebar visibility on their next login.
                </p>
                {roleChangeConfirm?.hasMultipleFacilities && (roleChangeConfirm?.to === 'TECHNICIAN' || roleChangeConfirm?.to === 'OPERATOR') && (
                  <p className="text-amber-600 font-medium">
                    Note: This role only allows one facility. The first assigned facility will be kept.
                  </p>
                )}
                {roleChangeConfirm?.to === 'ADMIN' && (
                  <p className="text-blue-600">
                    As an Admin, this user will have access to all facilities.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleRoleChangeCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleChangeConfirm}
              className="bg-[#005EF8] hover:bg-[#0047C2]"
              data-testid="confirm-role-change-btn"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* USER LIFECYCLE: Terminate Confirmation Dialog */}
      <AlertDialog open={!!terminateConfirm} onOpenChange={() => setTerminateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <UserX className="w-5 h-5" />
              Terminate User Access?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to terminate access for <strong>{terminateConfirm?.first_name} {terminateConfirm?.last_name}</strong>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                  <p className="font-medium text-amber-800">This will:</p>
                  <ul className="list-disc list-inside text-amber-700 mt-1">
                    <li>Immediately revoke their login access to this organization</li>
                    <li>Keep their history (work orders, notes, BRC entries) read-only for audit</li>
                    <li>They will not be able to log in until reactivated by an Admin</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lifecycleLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTerminate}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={lifecycleLoading}
              data-testid="confirm-terminate-btn"
            >
              {lifecycleLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Terminating...</>
              ) : (
                'Terminate Access'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* USER LIFECYCLE: Reactivate Confirmation Dialog */}
      <AlertDialog open={!!reactivateConfirm} onOpenChange={() => setReactivateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <UserCheck className="w-5 h-5" />
              Reactivate User?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to reactivate <strong>{reactivateConfirm?.first_name} {reactivateConfirm?.last_name}</strong>.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm">
                  <p className="font-medium text-green-800">This will:</p>
                  <ul className="list-disc list-inside text-green-700 mt-1">
                    <li>Restore their login access to this organization</li>
                    <li>Keep their previous role: <strong>{reactivateConfirm?.pre_termination_role || reactivateConfirm?.role}</strong></li>
                    <li>You can edit their role after reactivation if needed</li>
                  </ul>
                </div>
                {reactivateConfirm?.termination_date && (
                  <p className="text-sm text-[#5A5A5A]">
                    Terminated on: {new Date(reactivateConfirm.termination_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lifecycleLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReactivate}
              className="bg-green-600 hover:bg-green-700"
              disabled={lifecycleLoading}
              data-testid="confirm-reactivate-btn"
            >
              {lifecycleLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reactivating...</>
              ) : (
                'Reactivate User'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagementPage;
